// Authentication for the admin dashboard — the only thing this frontend is.
//
// Two ways in, both ending in the same place: this app holding the
// deployment's admin bootstrap key and sending it as `Authorization: Bearer`
// on every control-plane call (see api.ts). The control plane itself
// verifies nothing but that one shared secret (cloudish/server/src/auth/
// index.js) — it has no idea Auth0 exists.
//
//   Admin key — entered directly; the fallback for local dev or when Auth0
//               is unreachable.
//   Auth0     — PKCE login against *this app's own* backend's config
//               (GET/POST /session/*, not the control plane's /api/v1/*).
//               Once the token verifies and its email is on this
//               deployment's ADMIN_EMAILS, the backend hands back the same
//               admin key the manual path uses — Auth0 is just a friendlier
//               gate in front of it, scoped entirely to this repo.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { api, setTokenProvider, ApiError } from './api';
import type { AppConfig, AdminProfile, SessionConfig } from './types';
import { HOME_PATH } from './routes';

const ADMIN_KEY_STORAGE = 'cloudish.adminKey';

interface AuthState {
  config: AppConfig | null;
  sessionConfig: SessionConfig | null;
  profile: AdminProfile | null;
  loading: boolean;
  error: string | null;
  loginWithAuth0: () => Promise<void>;
  loginWithAdminKey: (key: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<AdminProfile | null>;
}

const AuthContext = createContext<AuthState | null>(null);

/** This app's own backend — same origin, distinct from api.ts's apiBase()
 *  (the control plane, cross-origin in production). */
async function sessionRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: { 'content-type': 'application/json', ...(options.headers || {}) },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error((payload as { error?: string })?.error || 'request failed');
  return payload as T;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [sessionConfig, setSessionConfig] = useState<SessionConfig | null>(null);
  const [profile, setProfileState] = useState<AdminProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const auth0Ref = useRef<Auth0Client | null>(null);
  const adminKeyRef = useRef<string | null>(localStorage.getItem(ADMIN_KEY_STORAGE));

  useEffect(() => {
    // The bootstrap key, once obtained (directly or via Auth0), is the only
    // thing ever sent to the control plane — see module comment.
    setTokenProvider(async () => adminKeyRef.current);
  }, []);

  const applyAdminKey = useCallback((key: string) => {
    adminKeyRef.current = key;
    localStorage.setItem(ADMIN_KEY_STORAGE, key);
  }, []);

  const clearAdminKey = useCallback(() => {
    adminKeyRef.current = null;
    localStorage.removeItem(ADMIN_KEY_STORAGE);
  }, []);

  // Admin status is confirmed by GET /api/v1/admin itself — there is no
  // separate "profile" endpoint, since the admin has no api_keys row.
  const refresh = useCallback(async () => {
    try {
      await api.get('/api/v1/admin');
      const next: AdminProfile = { admin: true, email: '' };
      setProfileState(next);
      return next;
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) setProfileState(null);
      else if (err instanceof Error) setError(err.message);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [appConfig, session] = await Promise.all([
          api.get<AppConfig>('/api/v1/config'),
          sessionRequest<SessionConfig>('/session/config').catch(
            () => ({ enabled: false, domain: '', clientId: '', audience: '' })
          ),
        ]);
        if (cancelled) return;
        setConfig(appConfig);
        setSessionConfig(session);

        if (session.enabled && session.domain && session.clientId) {
          const client = new Auth0Client({
            domain: session.domain,
            clientId: session.clientId,
            authorizationParams: {
              redirect_uri: `${window.location.origin}${HOME_PATH}`,
              audience: session.audience,
              scope: 'openid profile email',
            },
            cacheLocation: 'localstorage',
            useRefreshTokens: true,
          });
          auth0Ref.current = client;

          // Complete a redirect callback if we just came back from Auth0.
          if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
            try {
              const result = await client.handleRedirectCallback();
              const accessToken = await client.getTokenSilently();
              const { adminKey } = await sessionRequest<{ adminKey: string }>('/session/login', {
                method: 'POST',
                body: JSON.stringify({ token: accessToken }),
              });
              applyAdminKey(adminKey);
              const target = (result.appState as { returnTo?: string } | undefined)?.returnTo || HOME_PATH;
              navigate(target, { replace: true });
            } catch (err) {
              setError(err instanceof Error ? err.message : 'login failed');
              navigate(HOME_PATH, { replace: true });
            }
          }
        }

        await refresh();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'failed to load configuration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refresh, navigate, applyAdminKey]);

  const loginWithAuth0 = useCallback(async () => {
    const client = auth0Ref.current;
    if (!client) {
      setError('Auth0 is not configured on this deployment');
      return;
    }
    await client.loginWithRedirect({
      appState: { returnTo: `${window.location.pathname}${window.location.search}` },
    });
  }, []);

  const loginWithAdminKey = useCallback(
    async (key: string) => {
      const trimmed = key.trim();
      applyAdminKey(trimmed);
      try {
        await api.get('/api/v1/admin');
        setProfileState({ admin: true, email: '' });
        setError(null);
      } catch (err) {
        clearAdminKey();
        throw err;
      }
    },
    [applyAdminKey, clearAdminKey]
  );

  const logout = useCallback(async () => {
    clearAdminKey();
    setProfileState(null);
    const client = auth0Ref.current;
    if (client) {
      const isAuthenticated = await client.isAuthenticated().catch(() => false);
      if (isAuthenticated) {
        await client.logout({ logoutParams: { returnTo: window.location.origin } });
        return;
      }
    }
    window.location.href = '/';
  }, [clearAdminKey]);

  const value = useMemo<AuthState>(
    () => ({ config, sessionConfig, profile, loading, error, loginWithAuth0, loginWithAdminKey, logout, refresh }),
    [config, sessionConfig, profile, loading, error, loginWithAuth0, loginWithAdminKey, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
